import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ChatInputTool from '../../components/v2/chat-input-tool';
import BotMessage from '../../components/v2/bot-message';
import VideoPlayer from '../../components/v2/video-player';
import MathExpressionNew from '../../components/common/mathjax';
import SuccessBanner from '../../components/v2/success-banner';
import LessonQuiz from '../../components/v2/lesson-quiz';
import UserMessage from '../../components/v2/user-message';
import { usePlayLesson } from './usePlayLesson';
import { StepActions } from './sections/StepActions';
import { studyIcon, arrowFullscreen } from '../../assets/images';
import ZoomChartModal from '../../components/zoomable-image';
import { getChartSrc } from '../../utils/utils';
import loaderSvg from '../../assets/images/loader.svg';
import { unlockAudioContext } from '../../utils/audioManager';
import './play-lesson.scss';

export default function PlayLesson() {
  const location = useLocation();
  const {
    t,
    loading,
    lessonData,
    interactions,
    currentInteractionIndex,
    showInteractions,
    rewardedIds,
    greetingKey,
    askMeAboutKey,
    fullTopic,
    levelName,
    topicName,
    scrollContentRef,
    handleNextInteraction,
    handleWantExplanation,
    handleWantClue,
    handleGreetingComplete,
    markTypingDone,
    openModesModal,
    navigate,
    ROUTERS_PATH,
    correctAnswerIds,
    handleQuizAnswer,
    handleSendFreeText,
    isAgentLoading,
    activeAgentItemId,
    userInitials,
    userInfo,
    chatThreads,
    successMessageCounts,
    interactionStates,
    isVideoEligible,
    setIsVideoEligible,
    isResuming,
    gender,
    showCurtain,
    setShowCurtain,
    fadeCurtain,
    setFadeCurtain,
    activeQuizResponseItemId,
    setActiveQuizResponseItemId,
    playForInteraction,
    replayForInteraction,
    togglePauseForInteraction,
    stopLessonAudio
  } = usePlayLesson();
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);

  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (showCurtain && showInteractions) {
      // Fallback: If no scroll happens within 1.5s, drop it.
      fallbackTimeoutRef.current = setTimeout(() => {
        setFadeCurtain(true);
        setTimeout(() => setShowCurtain(false), 500);
      }, 1500); 

      return () => {
        if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      }
    }
  }, [showCurtain, showInteractions]);

  const handleScroll = () => {
    if (!showCurtain || !showInteractions) return;
    
    if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

    // When scrolling stops for 150ms, drop the curtain
    scrollTimeoutRef.current = setTimeout(() => {
      setFadeCurtain(true);
      setTimeout(() => setShowCurtain(false), 500);
    }, 150); 
  };

  if (!loading && !lessonData) {
    return (
      <div className="v2-play-lesson-loading">
        {t('lessonNotFound')}
        <button onClick={() => navigate(ROUTERS_PATH.SEARCH_LESSON)}>{t('return')}</button>
      </div>
    );
  }

  return (
    <div className="v2-play-lesson-layout" onClick={unlockAudioContext}>
      {/* Curtain Overlay for Resuming */}
      {showCurtain && (
        <div className={`v2-play-lesson-curtain ${fadeCurtain ? 'fade-out' : ''}`}>
          <img src={loaderSvg} alt="Loading..." style={{ width: 120, height: 120 }} />
          <span className="v2-play-lesson-curtain__text">{t('loadingLesson')}</span>
        </div>
      )}

      {/* Interactive Chat Area */}
      <main className="v2-play-lesson-chat" onScroll={handleScroll}>
        <div ref={scrollContentRef} className="v2-play-lesson-scroll-wrapper">

          {/* Typing BotMessage displays while greeting data is loading */}
          {loading && (
            <div className="v2-play-lesson-msg-wrapper">
              <BotMessage isLoading playAudio={false} />
            </div>
          )}

          {/* Greeting message */}
          {!loading && (
            <div className="v2-play-lesson-msg-wrapper">
              <BotMessage
                text={t(greetingKey, {
                  fullTopic: fullTopic,
                  levelName: levelName
                })}
                onComplete={handleGreetingComplete}
                playAudio={!isResuming}
                id="greeting"
                typingDuration={1500}
              />
            </div>
          )}

          {/* Render interactions sequentially */}
          {showInteractions && currentInteractionIndex >= 0 && interactions.slice(0, currentInteractionIndex + 1).map((item, index) => {
            const isCurrent = index === currentInteractionIndex;
            const isReward = rewardedIds.includes(item.id);
            const itemState = interactionStates[item.id] || {};

            return (
              <div key={item.id} className="v2-play-lesson-interaction fade-in">

                {/* 1. Step Title (Only for CONTENT types) */}
                {item.type === 'content' && item.title && (
                  <div className="v2-play-lesson-msg-wrapper">
                    <BotMessage
                      variant="title"
                      onComplete={() => isCurrent && markTypingDone(item.id, 'isTitleDone')}
                      skipAnimation={!isCurrent}
                      playAudio={isCurrent && !isResuming}
                      id={`title-${item.id}`}
                      typingDuration={1000}
                      onTogglePauseAudio={() => togglePauseForInteraction(item)}
                      onReplayAudio={() => replayForInteraction(item)}
                    >
                      <strong>{t('lessonStep')} {item.stepNumber}: <MathExpressionNew content={item.title} /></strong>
                    </BotMessage>
                  </div>
                )}

                {/* 2. Main content by type */}

                {/* VIDEO STEP */}
                {item.type === 'video' && (
                  <div className="v2-play-lesson-video-section">
                    <VideoPlayer
                      url={item.url}
                      onActionClick={(!chatThreads[item.id] || chatThreads[item.id].length === 0) ? handleNextInteraction : undefined}
                      onRewardEligibilityChange={setIsVideoEligible}
                      rewardThreshold={0}
                      disabled={!isCurrent}
                    />
                  </div>
                )}

                {/* CONTENT STEP — wait for title to finish if one exists */}
                {item.type === 'content' && (itemState.isTitleDone || !item.title || !isCurrent) && (
                  <div className="v2-play-lesson-msg-wrapper">
                    {item.video && (
                      <div className="v2-play-lesson-video-section" style={{ marginBottom: '16px' }}>
                        <VideoPlayer
                          url={item.video}
                          onRewardEligibilityChange={() => {}} // No reward for inline step videos by default
                          rewardThreshold={0}
                          disabled={!isCurrent}
                        />
                      </div>
                    )}
                    <BotMessage
                      variant="content"
                      onComplete={() => {
                        if (isCurrent) {
                          markTypingDone(item.id, 'isContentDone');
                          playForInteraction(item, isCurrent);
                        }
                      }}
                      skipAnimation={!isCurrent}
                      playAudio={isCurrent && !isResuming}
                      id={`content-${item.id}`}
                      typingDuration={1500}
                      onTogglePauseAudio={() => togglePauseForInteraction(item)}
                      onReplayAudio={() => replayForInteraction(item)}
                    >
                      <MathExpressionNew content={item.content} />
                    </BotMessage>
                  </div>
                )}

                {/* SUMMARY STEP */}
                {item.type === 'summary' && (
                  <div className="v2-play-lesson-msg-wrapper">
                    <BotMessage
                      variant="summary"
                      onComplete={() => isCurrent && markTypingDone(item.id, 'isContentDone')}
                      skipAnimation={!isCurrent}
                      playAudio={isCurrent && !isResuming}
                      id={`summary-${item.id}`}
                      typingDuration={1500}
                    >
                      <strong>{t('stepSummary')}:</strong><br />
                      <MathExpressionNew content={item.content} />
                    </BotMessage>
                  </div>
                )}

                {/* QUIZ STEP */}
                {item.type === 'quiz' && (
                  <div className="v2-play-lesson-msg-wrapper">
                    <LessonQuiz
                      quiz={item.quiz}
                      stepNumber={item.stepNumber}
                      onAnswer={(isCorrect, selectedOption) => {
                        stopLessonAudio();
                        if (item.id !== interactions[currentInteractionIndex]?.id) {
                          setActiveQuizResponseItemId(item.id);
                        }
                        handleQuizAnswer(isCorrect, selectedOption, item);
                      }}
                      onComplete={() => {
                        if (isCurrent) {
                          markTypingDone(item.id, 'isContentDone');
                          playForInteraction(item, isCurrent);
                        }
                      }}
                      disabled={itemState.isCorrect}
                      skipAnimation={!isCurrent}
                      playAudio={isCurrent && !isResuming}
                      id={`quiz-${item.id}`}
                      initialSelectedOption={itemState.selectedOption}
                      initialWrongOptions={itemState.wrongOptions}
                      onTogglePauseAudio={() => togglePauseForInteraction(item)}
                      onReplayAudio={() => replayForInteraction(item)}
                    />
                  </div>
                )}

                {/* 3. Chart (content steps only) */}
                {item.type === 'content' && item.chart && (itemState.isContentDone || !isCurrent) && (
                  <div className="v2-play-lesson-chart">
                    <div className="v2-play-lesson-chart__header">
                      <button
                        className="v2-play-lesson-chart__enlarge"
                        onClick={() => setEnlargedImage(getChartSrc(item.chart))}
                      >
                        <img src={arrowFullscreen} alt="enlarge" />
                      </button>
                    </div>
                    <div className="v2-play-lesson-chart__image-wrapper">
                      <img src={getChartSrc(item.chart)} alt="lesson chart" />
                    </div>
                  </div>
                )}

                {/* 4. Chat Threads (non-quiz interactions) */}
                {item.type !== 'quiz' && chatThreads[item.id]?.map((msg, i) => (
                  <div key={i} className="v2-play-lesson-msg-wrapper fade-in">
                    {msg.role === 'user' ? (
                      <UserMessage firstName={userInfo?.first_name} lastName={userInfo?.last_name}>
                        <MathExpressionNew content={msg.content} />
                      </UserMessage>
                    ) : (
                      <BotMessage
                        skipAnimation={!isCurrent}
                        playAudio={isCurrent && !isResuming}
                        id={`chat-${item.id}-${i}`}
                        isError={msg.isError}
                        onComplete={() => isCurrent && i === chatThreads[item.id].length - 1 && markTypingDone(item.id, 'isChatDone')}
                        text={msg.content === 'COMMUNICATION_ERROR' ? t('agentCommunicationError') : undefined}
                      >
                        {msg.content !== 'COMMUNICATION_ERROR' && <MathExpressionNew content={msg.content} />}
                      </BotMessage>
                    )}
                  </div>
                ))}

                {/* Video reward (shown after chat thread) */}
                {isReward && item.type === 'video' && (
                  <div className="v2-play-lesson-inline-reward fade-in">
                    <SuccessBanner points={itemState.pointsEarned ?? 20} message={t('v2RespectPopcorn')} animate={isCurrent && !isResuming} />
                  </div>
                )}

                {/* 5. Quiz chat thread — unified single path with inline banner */}
                {item.type === 'quiz' && activeQuizResponseItemId !== item.id && (() => {
                  const thread = chatThreads[item.id] || [];
                  const successAt = successMessageCounts[item.id] !== undefined
                    ? successMessageCounts[item.id]
                    : thread.length;
                  const isCorrect = correctAnswerIds.includes(item.id);

                  const renderSuccessBanner = () => {
                    const message = itemState.streakAchieved 
                      ? `${t('quizExcellentAnswer')} ${t('v2QuizStreakBonus', { count: itemState.streakAchieved })}`
                      : t('quizExcellentAnswer');

                    return (
                      <div className="v2-play-lesson-actions__reward-wrapper fade-in">
                        <SuccessBanner
                          key={`success-${item.id}`}
                          points={itemState.pointsEarned ?? 0}
                          message={message}
                          animate={isCurrent && !isResuming}
                        />
                      </div>
                    );
                  };

                  // If correct but thread is empty, show banner immediately
                  if (isCorrect && thread.length === 0) {
                    return renderSuccessBanner();
                  }

                  return (
                    <>
                      {thread.map((msg, i) => (
                        <React.Fragment key={i}>
                          {/* Insert success banner before the message at the correct position */}
                          {isCorrect && i === successAt && renderSuccessBanner()}
                          <div className="v2-play-lesson-msg-wrapper fade-in">
                            {msg.role === 'user' ? (
                              <UserMessage firstName={userInfo?.first_name} lastName={userInfo?.last_name}>
                                <MathExpressionNew content={msg.content} />
                              </UserMessage>
                            ) : (
                              <BotMessage
                                skipAnimation={!isCurrent}
                                playAudio={isCurrent && !isResuming}
                                id={`quiz-chat-${item.id}-${i}`}
                                isError={msg.isError}
                                onComplete={() => {
                                  if (isCurrent && i === (chatThreads[item.id]?.length || 0) - 1) {
                                    markTypingDone(item.id, 'isChatDone');
                                  }
                                }}
                                text={msg.content === 'COMMUNICATION_ERROR' ? t('agentCommunicationError') : undefined}
                              >
                                {msg.content !== 'COMMUNICATION_ERROR' && <MathExpressionNew content={msg.content} />}
                              </BotMessage>
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                      {/* Handle case where success banner is at the very end of the thread */}
                      {isCorrect && thread.length > 0 && successAt === thread.length && renderSuccessBanner()}
                    </>
                  );
                })()}

                {/* Loading Dots */}
                {isAgentLoading && activeAgentItemId === item.id && (
                  <div className="v2-play-lesson-msg-wrapper fade-in">
                    <BotMessage isLoading playAudio={false} />
                  </div>
                )}

                {/* 6. Action Buttons */}
                {activeQuizResponseItemId !== item.id && (
                  <StepActions
                    item={item}
                    isCurrent={isCurrent}
                    isAgentLoading={isAgentLoading}
                    interactionState={itemState}
                    chatThreadLength={chatThreads[item.id]?.length || 0}
                    isVideoEligible={isVideoEligible}
                    onNext={(isEligible) => handleNextInteraction(isEligible, item)}
                    onExplanation={() => handleWantExplanation(item)}
                    onClue={() => handleWantClue(item)}
                    gender={gender}
                  />
                )}
              </div>
            );
          })}

          {/* Bottom Response Block for answering historical quizzes */}
          {activeQuizResponseItemId && (() => {
            const quizItem = interactions.find(i => i.id === activeQuizResponseItemId);
            if (!quizItem) return null;
            const itemState = interactionStates[quizItem.id] || {};
            const thread = chatThreads[quizItem.id] || [];
            const successAt = successMessageCounts[quizItem.id] !== undefined
              ? successMessageCounts[quizItem.id]
              : thread.length;
            const isCorrect = correctAnswerIds.includes(quizItem.id);
            const firstName = userInfo?.first_name || '';

            const renderSuccessBanner = () => {
              const message = itemState.streakAchieved 
                ? `${t('quizExcellentAnswer')} ${t('v2QuizStreakBonus', { count: itemState.streakAchieved })}`
                : t('quizExcellentAnswer');

              return (
                <div className="v2-play-lesson-actions__reward-wrapper fade-in">
                  <SuccessBanner
                    key={`success-${quizItem.id}`}
                    points={itemState.pointsEarned ?? 0}
                    message={message}
                    animate={true}
                  />
                </div>
              );
            };

            return (
              <div className="v2-play-lesson-interaction v2-play-lesson-interaction--bottom-response fade-in">
                {/* 1. Success banner if correct and thread is empty */}
                {isCorrect && thread.length === 0 && renderSuccessBanner()}

                {/* 4. Chat thread messages */}
                {thread.map((msg, i) => (
                  <React.Fragment key={i}>
                    {isCorrect && i === successAt && renderSuccessBanner()}
                    <div className="v2-play-lesson-msg-wrapper fade-in">
                      {msg.role === 'user' ? (
                        <UserMessage firstName={userInfo?.first_name} lastName={userInfo?.last_name}>
                          <MathExpressionNew content={msg.content} />
                        </UserMessage>
                      ) : (
                        <BotMessage
                          skipAnimation={false}
                          playAudio={false}
                          id={`quiz-chat-${quizItem.id}-${i}`}
                          isError={msg.isError}
                          onComplete={() => {
                            if (i === thread.length - 1) {
                              markTypingDone(quizItem.id, 'isChatDone');
                            }
                          }}
                        >
                          {msg.content !== 'COMMUNICATION_ERROR' && <MathExpressionNew content={msg.content} />}
                        </BotMessage>
                      )}
                    </div>
                  </React.Fragment>
                ))}
                {isCorrect && thread.length > 0 && successAt === thread.length && renderSuccessBanner()}

                {/* 5. Loading Dots */}
                {isAgentLoading && activeAgentItemId === quizItem.id && (
                  <div className="v2-play-lesson-msg-wrapper fade-in">
                    <BotMessage isLoading playAudio={false} />
                  </div>
                )}

                {/* 6. Action buttons */}
                <StepActions
                  item={quizItem}
                  isCurrent={true} // Force true so buttons are clickable and visible
                  isAgentLoading={isAgentLoading}
                  interactionState={{ ...itemState, isContentDone: true }}
                  chatThreadLength={thread.length}
                  isVideoEligible={false}
                  onNext={(isEligible) => {
                    handleNextInteraction(isEligible, quizItem);
                    setActiveQuizResponseItemId(null); // Clear bottom response on continue!
                  }}
                  onExplanation={() => handleWantExplanation(quizItem)}
                  onClue={() => handleWantClue(quizItem)}
                  gender={gender}
                />
              </div>
            );
          })()}

          <div className="v2-play-lesson-bottom-anchor" />
          <div className="v2-play-lesson-bottom-spacer" />
        </div>
      </main>

      {/* Bottom Tool */}
      <ChatInputTool
        onSendMessage={handleSendFreeText}
        onMicClick={() => console.log('mic')}
        onAttachClick={() => console.log('attach')}
        onStudyModeClick={openModesModal}
        studyIcon={<img src={studyIcon} alt="study" />}
        highlightStudyMode={true}
        studyHighlightVariant="dark"
        placeholder={t(askMeAboutKey, { topic: topicName })}
        isLoading={isAgentLoading}
      />

      {enlargedImage && (
        <ZoomChartModal
          imageSrc={enlargedImage}
          onClose={() => setEnlargedImage(null)}
        />
      )}
    </div>
  );
}
